import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useAccountStore } from "@/features/account-management/store/account.store";
import { PLAN_AUDIENCE_IDS } from "./audiences";
import { FeatureFlagsContext, FeatureFlagsReadyContext } from "./context";
import { evaluateAll } from "./evaluate";
import { FEATURE_FLAGS } from "./feature-flags.constant";
import { fetchFlagConfig, readCachedConfig } from "./fetch-config";
import { cacheOrgHash, readCachedOrgHash, sha256Hex } from "./hash";
import { seedSnapshot, setSnapshot } from "./snapshot";
import type { TFlagSnapshot } from "./types";

export const FeatureFlagsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Evaluate flags against the *active* org, not the authenticated user's home
  // org. Internal admins can switch organization (persisted to the account
  // store + ADMIN_ORG_ID_KEY, mirroring the API's `Orgid` header), whereas
  // useAuthStore().orgId is frozen to the Firebase token's claim. account._id
  // is the same value admins enter into a flag's allow/blocklist.
  const orgId = useAccountStore((state) => state._id);
  // Plan targeting: planType lives in the same account store and is set
  // atomically with _id, so it is consistent with orgId in both the
  // synchronous cache seed and the async resolve below.
  const planType = useAccountStore((state) => state.planType);
  // `?? null` guards account.store's unchecked getPlanType cast — an unmapped
  // subscription title can yield a planType outside the union at runtime.
  const planId = PLAN_AUDIENCE_IDS[planType] ?? null;
  // The org+plan the last fetch resolved for: `ready` until they change.
  const key = orgId ? `${orgId}:${planId}` : null;
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  // Seed synchronously from the cached config so returning users render
  // immediately with their last-known flags instead of blocking on the CDN.
  // The fetch effect below overwrites this with a fresh resolve on mount.
  // Only a genuine cold start (no cache yet) falls through to the `null`
  // block, which avoids a flash of all-off → on for never-cached loads.
  const [snapshot, setLocalSnapshot] = useState<TFlagSnapshot | null>(() => {
    if (!orgId) return null;
    const cached = readCachedConfig();
    if (!cached) return null;
    // Synchronous seed: use the cached org-hash if present (returning users);
    // null on a cold start still resolves rollout flags and raw-id allowlists.
    const seeded = evaluateAll(cached, orgId, readCachedOrgHash(orgId), planId);
    // Publish the same values to the module-level snapshot the hook-free
    // readers use, so `isOnFeatureFlag`/`getSnapshot` and `useFeatureFlag`
    // never answer differently for a flag the cache covers. See
    // `seedSnapshot` in snapshot.ts for why it does not notify subscribers.
    seedSnapshot(seeded);
    return seeded;
  });

  useEffect(() => {
    if (!orgId) {
      setSnapshot({});
      // Unchanged behaviour: with no org, children render with every flag off.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalSnapshot({});
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const orgHash = await sha256Hex(orgId);
      if (cancelled) return;
      cacheOrgHash(orgId, orgHash);
      const config = await fetchFlagConfig();
      if (cancelled) return;
      const resolved = evaluateAll(config, orgId, orgHash, planId);
      setSnapshot(resolved);
      setLocalSnapshot(resolved);
      setResolvedKey(`${orgId}:${planId}`);
    };

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [orgId, planId]);

  // Every Sentry event says which text editor the org runs, so errors from
  // the ProseMirror beta can be filtered and compared with Froala orgs.
  const proseMirrorText = snapshot?.[FEATURE_FLAGS.PROSEMIRROR_TEXT]?.on;
  useEffect(() => {
    Sentry.setTag("prosemirror_text", proseMirrorText ? "on" : "off");
  }, [proseMirrorText]);

  if (snapshot === null) return null;

  return (
    <FeatureFlagsContext.Provider value={snapshot}>
      <FeatureFlagsReadyContext.Provider
        value={key !== null && resolvedKey === key}
      >
        {children}
      </FeatureFlagsReadyContext.Provider>
    </FeatureFlagsContext.Provider>
  );
};
